import React, { useState, Fragment, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import * as Select from '@radix-ui/react-select';
import * as S from './styles';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { View } from '../Basic/View/view';
import { Either } from '../../utils/propsUtil';
import { HiddenInput } from '../Input/styles';
import * as Form from '@radix-ui/react-form';
import { useTranslation } from "react-i18next";
import { Checkbox } from '../Checkbox';
import "./style.css"
import { Row } from '../Basic/Row/row';
import { InputTitle } from '../Form/Components/inputTitle';
import { ValueCheck } from 'domain/utils/ReactUtils';
import { useTheme } from '../../utils/context/themeContext';
import { InputMensage } from '../Form/Components/InputMensage';
import { Theme } from '../../styles/themes/types';
import { none } from '../../utils/componentUtil';

export interface IOptionsIten {
  id: string | number | boolean;
  nome: string;
  default?: boolean;
  color?: string;
  textColor?: string;
}


export interface IOptionsGroup {
  idProperty?: string;
  nomeGrupo: string;
  options?: IOptionsIten[] | undefined;
}

/* eslint-disable no-unused-vars */
export interface Props0<T extends HTMLElement = HTMLElement> extends React.HTMLAttributes<T> {
  label?: string;
  placeholder?: string;
  optionsGroup: IOptionsGroup[];
  onChangeValue: ((value: string) => void) | ((value: string[]) => void)
  multiple?: boolean
  full?: boolean;
  forceValueText?: string
  msgError?: string | undefined
  insideModal?: boolean;
}

interface Props1 {
  enableForm: boolean;
  name: string;
}

interface Props2 { }

export interface OptionsHandle {
  Reset: () => void;
}

const defaultValue: SelectProps = {
  multiple: false
} as SelectProps

export type SelectProps = React.ComponentProps<typeof Select.Root> & Props0 & Either<Props1, Props2>


function handleChange(props: SelectProps, value: string | string[]) {

  if (typeof value === "string" && typeof props.onChangeValue === "function") {
    (props.onChangeValue as (value: string) => void)(value);

  } else if (Array.isArray(value) && typeof props.onChangeValue === "function") {

    (props.onChangeValue as (value: string[]) => void)(value);
  }
}

export const Options = React.memo(forwardRef<OptionsHandle, SelectProps>(({ /* children ,*/ ...props }, ref) => {
  props = { ...defaultValue, ...props }
  const { theme } = useTheme();
  const [value, setValue] = useState<string | string[]>();
  const [reset, setReset] = useState(false)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (props.value == undefined) { return }

    setValue(props.value)
  }, []);

  useEffect(() => {
    setReset(false)
  }, [reset])

  function Reset() {
    setReset(true)
  }

  useImperativeHandle(ref, () => ({
    Reset,
  }));

  function handleValue(value: string | string[]) {
    handleChange(props, value)
    setValue(value)
    setError(false)
  }

  function handleError() {
    setError(true)
  }

  return (
    <View {...props}>

      {props.multiple == false ?
        <SelectOptions theme={theme} erro={error} reset={reset} handleValue={handleValue} {...props} />
        :
        <SelectCheckBox theme={theme} erro={error} reset={reset} handleValue={handleValue} {...props} />

      }

      {props.enableForm && (
        <Form.Control onInvalid={handleError} asChild>
          <HiddenInput name={props.name} onChange={none} value={value} type="text" required />
        </Form.Control>
      )}
    </View>
  );
}));

interface ISelectPropsExtend {
  reset: boolean
  handleValue: (value: string | string[]) => void
  erro: boolean
  theme: Theme
}

export type SelectPropsExtend = ISelectPropsExtend & SelectProps;

function SelectOptions( /* children, */ props: SelectPropsExtend) {
  /* const { i18n, t } = useTranslation(); */

  function handleSelectValue(value: string) {
    props.handleValue(value)
  }

  useEffect(() => {
    if (props.optionsGroup.length === 1 && props.optionsGroup[0].options?.length === 1) {

      handleSelectValue(String(props.optionsGroup[0].options[0].id))
    }
  }, [props.optionsGroup])

  return (

    <Select.Root value={props.value} onValueChange={handleSelectValue}>
      {props.label ?
        <S.SelectHeader>
          <InputTitle form={props.enableForm} label={props.title} />
          <InputMensage form={props.enableForm} label={props.label} type={undefined} />
          {props.msgError && props.erro && <InputMensage style={{ color: props.theme.colors.warning }} form={props.enableForm} label={props.msgError} type={"valueMissing"} />}
        </S.SelectHeader>
        : <></>}
      <S.SelectTrigger color='red' full={props.full || false} aria-label={props.label}>
        <S.ValuePlaceHolder ><Select.Value placeholder={props.forceValueText ? props.forceValueText : props.placeholder} /></S.ValuePlaceHolder>
        <S.SelectIcon>
          <ChevronDownIcon />
        </S.SelectIcon>
      </S.SelectTrigger>
      <Select.Portal>
        <S.SelectContent insideModal={props.insideModal}>
          <S.SelectScrollUpButton>
            <ChevronUpIcon />
          </S.SelectScrollUpButton>
          <S.SelectViewport>
            {props.reset == false && props.optionsGroup.map((oG: IOptionsGroup, i: number) =>
              <Fragment key={i}>
                <Select.Group>
                  {props.optionsGroup.length > 1 && <S.SelectLabel>{oG.nomeGrupo}</S.SelectLabel>}
                  {oG.options && oG.options.map((g, i) => (
                    <SelectItem color={g.color || ''} textColor={g.textColor || ''} data-testid="options-SelectItem" defaultChecked={props.optionsGroup.length === 1 && oG.options?.length === 1 ? true : g.default} key={i} value={String(g.id)}>
                      {g.nome}
                    </SelectItem>
                  ))}
                </Select.Group>
                {props.optionsGroup.length > 1 && <S.SelectSeparator />}
              </Fragment>
            )}
          </S.SelectViewport>
          <S.SelectScrollDownButton>
            <ChevronDownIcon />
          </S.SelectScrollDownButton>
        </S.SelectContent>
      </Select.Portal>
    </Select.Root >

  )
}

function SelectCheckBox({ /* children, */ ...props }: SelectPropsExtend) {
  const { t } = useTranslation();


  function handleDefaultCheked() {
    const copy = [...props.optionsGroup]
    if (copy.length === 1 && copy[0].options?.length === 1) {

      copy[0].options[0].default = true;
    }

    return copy
      .flatMap(group => group.options?.filter(option => option.default == true) || []);
  }

  const [checked, setChecked] = useState<IOptionsIten[]>();

  useEffect(() => {
    if (props.optionsGroup.length === 1 && props.optionsGroup[0].options?.length === 1) {

      setChecked(handleDefaultCheked())
    }
  }, [props.optionsGroup])

  const [colapse, setColapse] = useState(false);

  const divButtonRef = useRef<HTMLDivElement>(null);
  const divViewPortRef = useRef<HTMLDivElement>(null);

  function handleClickOutside(event: MouseEvent) {
    if (divButtonRef.current && event.target instanceof Node && divButtonRef.current.contains(event.target)) { return }

    if (divViewPortRef.current && event.target instanceof Node && !divViewPortRef.current.contains(event.target)) {
      handleColapse(false)
    }
  }

  function handleToggleColapse() {
    handleColapse(!colapse);
  }

  function handleColapse(value: boolean) {
    setColapse(value)
    if (value == true) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
  }

  function handleToggleItem(item: IOptionsIten) {
    return () => {
      setChecked(prevChecked => {
        const checkedList = prevChecked ?? []; // Garante que seja sempre um array

        // Verifica se o item já está na lista
        const isChecked = checkedList.some(checkedItem => checkedItem.id === item.id);

        // Se o item estiver na lista, remove-o; caso contrário, adiciona-o
        return isChecked
          ? checkedList.filter(checkedItem => checkedItem.id !== item.id)
          : [...checkedList, item];
      });
    }
  }

  useEffect(() => { //Limpa
    if (props.reset)
      setChecked([])
  }, [props.reset])

  useEffect(() => {
    if (checked == null) { return }
    props.handleValue(checked.map(e => e.id as string));
  }, [checked])

  const { theme } = useTheme();

  return (
    <S.CheckRoot>
      {props.msgError && props.erro && <InputMensage style={{ color: props.theme.colors.warning }} form={props.enableForm} label={props.msgError} type={"valueMissing"} />}
      <S.CheckHead ref={divButtonRef} onClick={handleToggleColapse}>
        <S.CheckTitle>
          {props.placeholder}
        </S.CheckTitle>
      </S.CheckHead>

      <S.CheckViewport full={props.full} ref={divViewPortRef} className={`${colapse ? "viewPortOpen" : ""}`}>
        {props.reset == false && props.optionsGroup.map((oG, i) =>
          <Fragment key={i}>
            <S.CheckGroup key={i}>
              {props.optionsGroup.length > 1 && <label>{oG.nomeGrupo}</label>}
              {oG.options ?
                (oG.options.map((g) => (
                  <Row>
                    <Checkbox form={props.enableForm} value={ValueCheck(checked, g, 'id')} setValue={handleToggleItem(g)} style={{ marginRight: 10, color: theme.colors.primary, }}>
                      <InputTitle form={props.enableForm} label={g.nome} />
                    </Checkbox>
                  </Row>
                ))) : (
                  <label>
                    {t("Carregando")}
                  </label>
                )}
            </S.CheckGroup>
            {props.optionsGroup.length > 1 && <S.SelectSeparator />}
          </Fragment>
        )}
      </S.CheckViewport>
    </S.CheckRoot >
  )
}

// Define SelectItem component to render each selectable item in the dropdown
type SelectItemProps = React.ComponentProps<typeof Select.Item> & {
  className?: string;
  value: string;
  color?: string;
  textColor?: string;
};

const SelectItem = React.forwardRef<HTMLDivElement, SelectItemProps>(
  ({ children, /* className, */ value, color, textColor, ...props }, forwardedRef) => (
    <S.SelectItem value={value} color={color} textColor={textColor} {...props} ref={forwardedRef}>
      <S.SelectItemText>{children}</S.SelectItemText>
      <S.SelectItemIndicator>
        <CheckIcon />
      </S.SelectItemIndicator>
    </S.SelectItem>
  )
);
SelectItem.displayName = 'SelectItem';